import { getFreshToken } from '@/lib/firebase';

/**
 * Download a Stripe Quote Generator quote's PDF — `GET /api/stripe-quotes/:quoteId/quote.pdf`.
 *
 * Fetched with the bearer token and handed to the browser as a blob, for the same reasons as
 * the International Requests PDF (features/intl-requests/quote-pdf.ts): a plain link cannot send
 * the Authorization header, and Stripe's own PDF URL is a bearer link outside our auth.
 */
const API_BASE = (import.meta.env.VITE_GRAPHQL_URL as string).replace(/\/graphql\/?$/, '');

export async function downloadStripeQuotePdf(quoteId: string): Promise<void> {
  const { token } = await getFreshToken();

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/stripe-quotes/${encodeURIComponent(quoteId)}/quote.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new Error(
      'Could not reach the staff API — the request was rejected before it arrived (network or ' +
        'API gateway).',
    );
  }

  if (!res.ok) {
    let msg = `Could not download the quote (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      const m = Array.isArray(body.message) ? body.message.join(', ') : body.message;
      if (m) msg = m;
    } catch {
      // A non-JSON error page — the status line is all there is.
    }
    throw new Error(msg);
  }

  const cd = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(cd)?.[1] || `optisigns-quote-${quoteId}.pdf`;
  const url = URL.createObjectURL(await res.blob());
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
